<script setup lang="ts">
import { SplitPanel } from '@directus/vue-split-panel';
import { computed, useTemplateRef } from 'vue';
import { SIDEBAR_DEFAULT_SIZE, SIDEBAR_MAX_SIZE, SIDEBAR_MIN_SIZE, usePaneStore } from '~~/layers/chrome/stores/pane';

const paneStore = usePaneStore();

const splitContainer = useTemplateRef<HTMLElement>('splitContainer');
const drawerContainer = useTemplateRef<HTMLElement>('drawerContainer');

const teleportTarget = computed(() => (paneStore.sidebarUseSplitPanel ? splitContainer.value : drawerContainer.value));
</script>

<template>
	<SplitPanel
		v-model:size="paneStore.sidebarSize"
		v-model:collapsed="paneStore.sidebarSplitPanelCollapsed"
		class="h-full"
		size-unit="px"
		collapsible
		:collapse-threshold="70"
		:min-size="SIDEBAR_MIN_SIZE"
		:max-size="SIDEBAR_MAX_SIZE"
		:snap-points="[SIDEBAR_DEFAULT_SIZE]"
		:snap-threshold="6"
		:ui="{
			divider: 'focus-visible:ring-1',
			start: 'flex flex-col pbe-2',
			end: 'flex flex-col pe-2 pbe-2',
		}"
		divider-hit-area="24px"
		:transition-duration="150"
		primary="start"
		:disabled="!paneStore.sidebarUseSplitPanel"
	>
		<template #start>
			<div v-if="paneStore.sidebarUseSplitPanel" class="flex flex-col block-full">
				<ProjectChromeSidebarHeader class="shrink-0" />

				<div ref="splitContainer" class="flex flex-1 grow flex-col min-block-0" />
			</div>
		</template>

		<template #divider>
			<ProjectChromeDivider v-show="paneStore.sidebarUseSplitPanel" :show-border="false" :collapsed="paneStore.sidebarSplitPanelCollapsed" placement="start" />
		</template>

		<template #end>
			<ProjectChromePage>
				<template #header-title>
					<slot name="header-title" />
				</template>

				<slot />
			</ProjectChromePage>
		</template>
	</SplitPanel>

	<DDrawer
		v-if="!paneStore.sidebarUseSplitPanel"
		:open="paneStore.sidebarDrawerVisible"
		direction="left"
		:handle="false"
		:ui="{ content: `min-w-[calc(100vw-var(--spacebar-width))] sm:min-w-62.5` }"
		@update:open="paneStore.sidebarDrawerVisible = $event"
	>
		<template #content>
			<div class="flex size-full flex-col overflow-hidden rounded-lg bg-default ring ring-default">
				<div ref="drawerContainer" class="flex h-full flex-col" />
			</div>
		</template>
	</DDrawer>

	<div class="hidden">
		<Teleport :to="teleportTarget" :disabled="!teleportTarget" defer>
			<slot name="sidebar" />
		</Teleport>
	</div>
</template>
